import i18n from "../i18n";

const USER_MESSAGE_KEYS = Object.freeze({
    DOWNLOAD_CODE_NOT_FOUND: "messages.download_code_not_found",
    SALE_SERVICE_CONFIGURATION_NOT_FOUND: "messages.album_service_not_ready",
    CONFIGURATION_NOT_FOUND: "messages.album_service_not_ready",
    INVALID_CONFIGURATION: "messages.album_service_not_ready",
    SERVICE_LOCATIONS_NOT_FOUND: "messages.album_service_not_ready",
    NOT_FOUND: "messages.album_media_not_found",
    VIDEO_NOT_FOUND: "messages.album_media_not_found",
    VIDEO_TAG_NOT_FOUND: "messages.album_media_not_found",
    VIDEO_NOT_ASSOCIATED_WITH_CODE: "messages.album_media_not_found",
    VIDEO_PARK_MISMATCH: "messages.album_media_not_found",
    PARK_NOT_FOUND: "messages.park_not_available",
    PARK_NOT_ELIGIBLE: "messages.park_not_eligible",
    PARK_LIMIT_REACHED: "messages.park_limit_reached",
    VISIT_PARK_NOT_ADDED: "messages.park_unlock_not_updated",
    VISIT_PARK_NOT_SELECTED: "messages.park_not_unlocked",
    MEDIA_DOWNLOAD_EXCEEDS_TOTAL: "messages.media_unlock_error",
    MEDIA_LOG_NOT_CREATED: "messages.media_unlock_error",
    INVALID_JSON: "messages.request_could_not_be_processed",
    INVALID_REQUEST: "messages.request_could_not_be_processed",
    INVALID_UNLOCK_MODE: "messages.request_could_not_be_processed",
    INVALID_UNLOCK_TARGET: "messages.request_could_not_be_processed",
    INVALID_ALBUM_KEY_CONTEXT: "messages.request_could_not_be_processed",
    INVALID_ALBUM_KEY: "messages.album_session_expired",
    ALBUM_KEY_EXPIRED: "messages.album_session_expired",
    UNAUTHORIZED: "messages.session_expired",
    FORBIDDEN: "messages.session_expired",
});

const TEMPORARY_ERROR_PREFIXES = Object.freeze([
    "DATABASE_",
    "DEPENDENCY_",
    "UPSTREAM_",
]);

/**
 * Converts Axios, Laravel and AWS API failures into one predictable shape.
 *
 * The public API body contains exactly error_code, message and details.
 * User-facing components must use GetApiErrorMessage instead of displaying
 * the technical message or details directly.
 *
 * @param {*} error Original thrown value.
 * @param {string} fallbackMessage Message used when no public message exists.
 * @returns {{status:number|null,code:string,message:string,details:string,requestId:string|null,retryable:boolean,isNetworkError:boolean}}
 */
export function NormalizeApiError(
    error,
    fallbackMessage = "The service is temporarily unavailable."
) {
    const response = error && error.response ? error.response : null;
    const data = response && response.data && typeof response.data === "object"
        ? response.data
        : {};
    const status = response && Number.isInteger(response.status)
        ? response.status
        : null;
    const networkErrorCodes = new Set([
        "ECONNABORTED",
        "ECONNRESET",
        "ECONNREFUSED",
        "ENETDOWN",
        "ENETUNREACH",
        "EHOSTUNREACH",
        "ETIMEDOUT",
        "ERR_NETWORK",
    ]);
    const errorCode = String(error?.code || "").toUpperCase();
    const isNetworkError = !response && Boolean(
        error?.isAxiosError ||
        error?.request ||
        networkErrorCodes.has(errorCode)
    );
    const responseHeaders = response && response.headers ? response.headers : {};
    const rawDetails = data.details;
    const details = typeof rawDetails === "string" && rawDetails.trim()
        ? rawDetails.trim()
        : Array.isArray(rawDetails)
            ? rawDetails
                .map((item) => {
                    if (typeof item === "string") return item;
                    if (!item || typeof item !== "object") return "";
                    const field = item.field || item.path;
                    const message = item.message || item.description || item.type;
                    return field && message ? `${field}: ${message}` : message || "";
                })
                .filter(Boolean)
                .join("; ")
            : "No additional details are available.";

    return {
        status,
        code: data.error_code || (isNetworkError ? "NETWORK_ERROR" : status ? `HTTP_${status}` : "CLIENT_ERROR"),
        message: data.message || (error && error.message) || fallbackMessage,
        details,
        requestId:
            responseHeaders["x-request-id"] ||
            responseHeaders["X-Request-Id"] ||
            null,
        retryable: isNetworkError || [429, 502, 503, 504].includes(status),
        isNetworkError,
    };
}

/**
 * Adds the normalized error to the original Axios error without replacing it.
 *
 * @param {*} error Original error.
 * @returns {*} Same error instance.
 */
export function AttachApiError(error) {
    if (error && typeof error === "object") {
        error.apiError = NormalizeApiError(error);
    }

    return error;
}

/**
 * Resolves the translation key that is safe and useful for a site visitor.
 *
 * @param {Object} normalizedError Normalized API error.
 * @returns {string|null} Translation key or null when the caller fallback should be used.
 */
export function ResolveApiErrorMessageKey(normalizedError) {
    const code = String(normalizedError?.code || "").toUpperCase();
    const status = Number(normalizedError?.status || 0);

    if (USER_MESSAGE_KEYS[code]) {
        return USER_MESSAGE_KEYS[code];
    }

    if (
        normalizedError?.isNetworkError ||
        status >= 500 ||
        code === "NETWORK_ERROR" ||
        TEMPORARY_ERROR_PREFIXES.some((prefix) => code.startsWith(prefix))
    ) {
        return "messages.service_temporarily_unavailable";
    }

    if ([400, 405, 422].includes(status)) {
        return "messages.request_could_not_be_processed";
    }

    if (status === 401 || status === 403) {
        return "messages.session_expired";
    }

    if (status === 404) {
        return "messages.album_media_not_found";
    }

    return null;
}

/**
 * Returns a localized message intended for a site visitor.
 *
 * Technical upstream messages and database details are deliberately excluded.
 *
 * @param {*} error Original thrown value.
 * @param {string} fallbackMessage Context-specific fallback message.
 * @param {boolean} preferFallback Whether to use the supplied fallback directly.
 * @returns {string} Localized visitor-facing message.
 */
export function GetApiErrorMessage(
    error,
    fallbackMessage,
    preferFallback = false
) {
    if (preferFallback && fallbackMessage) {
        return fallbackMessage;
    }

    const normalizedError = error?.apiError || NormalizeApiError(error, fallbackMessage);
    const key = ResolveApiErrorMessageKey(normalizedError);

    if (key) {
        return i18n.t(key);
    }

    return fallbackMessage || i18n.t("messages.service_temporarily_unavailable");
}
